'use client';

import { useEffect, useState } from 'react';
import { getScrollPercent } from 'utils/scroll';
import { throttle } from 'utils/throttle';
import type { ToastTriggerOptions } from './types';

/** Applies the optional delay and scroll-depth gates to the toast. */
export const useToastTrigger = ({ scrollDepthPercent, timeOnSiteSeconds }: ToastTriggerOptions): boolean => {
  // A depth of 0 drops the scroll gate rather than setting it to the top of the
  // page: the delay alone reveals the toast, and scrolling up no longer hides it.
  const scrollGateOff = scrollDepthPercent === 0;
  const [scrolledIntoRange, setScrolledIntoRange] = useState(false);
  const [delayElapsed, setDelayElapsed] = useState(timeOnSiteSeconds === undefined);

  useEffect(() => {
    if (timeOnSiteSeconds === undefined) {
      setDelayElapsed(true);
      return;
    }

    setDelayElapsed(false);
    const timer = setTimeout(() => setDelayElapsed(true), timeOnSiteSeconds * 1000);
    return () => clearTimeout(timer);
  }, [timeOnSiteSeconds]);

  useEffect(() => {
    if (scrollGateOff) return;

    setScrolledIntoRange(false);

    let lastScrollY = window.scrollY;
    let cancelled = false;

    const handleScroll = throttle(() => {
      if (cancelled) return;

      const currentScrollY = window.scrollY;
      const pastDepth = scrollDepthPercent === undefined || getScrollPercent() >= scrollDepthPercent;

      if (currentScrollY > lastScrollY && pastDepth) {
        setScrolledIntoRange(true);
      } else if (currentScrollY < lastScrollY) {
        setScrolledIntoRange(false);
      }

      lastScrollY = currentScrollY;
    }, 100);

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      cancelled = true;
      window.removeEventListener('scroll', handleScroll);
    };
  }, [scrollDepthPercent, scrollGateOff]);

  return (scrollGateOff || scrolledIntoRange) && delayElapsed;
};
